'use client'

import * as React from 'react'
import { useSavedCompare, COMPARE_MAX, type SavedItem } from '@/components/homepage-2026/saved-compare-store'
import type { Notice } from './blocks/Notice'

const NoticeRenderer = React.lazy(() => import('./blocks/Notice'))

export { COMPARE_MAX }
export type ShopItem = { id?: string; fin: string; brand: string; title: string; href: string; img?: string; price?: string; priceCents?: number }
type Panel = null | 'compare' | 'saved' | 'menu' | 'search' | 'frosty'
const toShop = (p: SavedItem): ShopItem => ({ ...p, img: p.image, price: p.priceCents > 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.priceCents / 100) : undefined })
const toSaved = (p: ShopItem): SavedItem => ({ id: p.id || p.fin, fin: p.fin, brand: p.brand, title: p.title, href: p.href, image: p.img || '', priceCents: p.priceCents ?? Math.round(Number((p.price || '').replace(/[^0-9.]/g, '')) * 100) })
type Shop = {
  compare: ShopItem[]; saved: ShopItem[]
  isCompared: (p: Pick<ShopItem, 'id' | 'fin' | 'href'>) => boolean; isSaved: (p: Pick<ShopItem, 'id' | 'fin' | 'href'>) => boolean
  toggleCompare: (p: ShopItem) => void; toggleSaved: (p: ShopItem) => void
  clearCompare: () => void; panel: Panel
  open: (p: Panel, opener?: HTMLElement | null) => void; close: () => void
  notify: (message: string, description: string, tone?: Notice['tone']) => void
}
const Ctx = React.createContext<Shop | null>(null)

export function ShopProvider({ children }: { children: React.ReactNode }) {
  const store = useSavedCompare()
  const [panel, setPanel] = React.useState<Panel>(null)
  const [notice, setNotice] = React.useState<Notice | null>(null)
  const noticeId = React.useRef(0)
  const notify = React.useCallback((message: string, description: string, tone: Notice['tone'] = 'info') => {
    setNotice({ id: ++noticeId.current, message, description, tone })
  }, [])
  const opener = React.useRef<HTMLElement | null>(null)
  const open = React.useCallback((next: Panel, el?: HTMLElement | null) => {
    opener.current = el ?? document.activeElement as HTMLElement | null
    if (next === 'frosty') { window.dispatchEvent(new Event('fridge:open-frosty')); return }
    setPanel(next)
  }, [])
  const close = React.useCallback(() => {
    setPanel(null)
    window.setTimeout(() => opener.current?.focus(), 0)
  }, [])
  const value: Shop = {
    saved: store.saved.map(toShop), compare: store.compare.map(toShop), panel, open, close, notify,
    isSaved: p => store.isSaved(p) || store.saved.some(item => item.fin === p.fin),
    isCompared: p => store.isCompared(p) || store.compare.some(item => item.fin === p.fin),
    toggleSaved: p => store.toggleSaved(toSaved(p)),
    toggleCompare: p => {
      if (!store.isCompared(toSaved(p)) && store.compare.length >= COMPARE_MAX) {
        notify(`Compare holds ${COMPARE_MAX} models`, 'Remove one to add another.', 'warning')
        return
      }
      store.toggleCompare(toSaved(p))
    },
    clearCompare: store.clearCompare,
  }
  return <Ctx.Provider value={value}>{children}{notice ? <React.Suspense fallback={null}><NoticeRenderer notice={notice} /></React.Suspense> : null}</Ctx.Provider>
}
export function useShop() {
  const value = React.useContext(Ctx)
  if (!value) throw new Error('Shopping controls need ShopProvider')
  return value
}
